import { Field, Booking, TimeSlot, OwnerStats, Amenities, DayKey } from '../types';
import { getOwnerProfileById, getSession } from './authStore';
import { notifyLocalDataChanged, syncFieldsToSupabase } from './supabaseSync';
import { logAuditEvent } from './auditStore';
const FIELDS_KEY = 'halisaha_fields';

const defaultAmenities: Amenities = {
  parking: false,
  shower: false,
  hotWater: false,
  lighting: false,
  cafeteria: false,
  lockerRoom: false,
  tribune: false,
  toilet: false,
  prayerRoom: false,
  firstAid: false,
  wifi: false,
  security: false,
  cameraSecurity: false,
  shoeRental: false,
  ballProvided: false,
  vestProvided: false,
};

export function createDefaultAmenities(): Amenities {
  return { ...defaultAmenities };
}

export const ISTANBUL_DISTRICT_COORDINATES: Record<string, { lat: number; lng: number }> = {
  Adalar: { lat: 40.876, lng: 29.091 },
  Arnavutkoy: { lat: 41.185, lng: 28.74 },
  Atasehir: { lat: 40.992, lng: 29.124 },
  Avcilar: { lat: 40.979, lng: 28.721 },
  Bagcilar: { lat: 41.039, lng: 28.856 },
  Bahcelievler: { lat: 41.001, lng: 28.859 },
  Bakirkoy: { lat: 40.98, lng: 28.872 },
  Basaksehir: { lat: 41.092, lng: 28.807 },
  Bayrampasa: { lat: 41.048, lng: 28.9 },
  Besiktas: { lat: 41.043, lng: 29.009 },
  Beykoz: { lat: 41.138, lng: 29.098 },
  Beylikduzu: { lat: 40.983, lng: 28.649 },
  Beyoglu: { lat: 41.037, lng: 28.974 },
  Buyukcekmece: { lat: 41.021, lng: 28.585 },
  Catalca: { lat: 41.143, lng: 28.461 },
  Cekmekoy: { lat: 41.033, lng: 29.189 },
  Esenler: { lat: 41.045, lng: 28.877 },
  Esenyurt: { lat: 41.034, lng: 28.68 },
  Eyupsultan: { lat: 41.056, lng: 28.934 },
  Fatih: { lat: 41.017, lng: 28.949 },
  Gaziosmanpasa: { lat: 41.075, lng: 28.917 },
  Gungoren: { lat: 41.017, lng: 28.874 },
  Kadikoy: { lat: 40.983, lng: 29.03 },
  Kagithane: { lat: 41.082, lng: 28.968 },
  Kartal: { lat: 40.903, lng: 29.187 },
  Kucukcekmece: { lat: 41.001, lng: 28.778 },
  Maltepe: { lat: 40.935, lng: 29.155 },
  Pendik: { lat: 40.878, lng: 29.246 },
  Sancaktepe: { lat: 41.001, lng: 29.211 },
  Sariyer: { lat: 41.167, lng: 29.052 },
  Silivri: { lat: 41.073, lng: 28.247 },
  Sisli: { lat: 41.06, lng: 28.987 },
  Sultanbeyli: { lat: 40.968, lng: 29.268 },
  Sultangazi: { lat: 41.101, lng: 28.871 },
  Sile: { lat: 41.175, lng: 29.613 },
  Tuzla: { lat: 40.816, lng: 29.3 },
  Umraniye: { lat: 41.024, lng: 29.116 },
  Uskudar: { lat: 41.022, lng: 29.015 },
  Zeytinburnu: { lat: 40.993, lng: 28.904 },
};

const DISTRICT_NAME_TO_COORD_KEY: Record<string, keyof typeof ISTANBUL_DISTRICT_COORDINATES> = {
  Adalar: 'Adalar',
  Arnavutköy: 'Arnavutkoy',
  Ataşehir: 'Atasehir',
  Avcılar: 'Avcilar',
  Bağcılar: 'Bagcilar',
  Bahçelievler: 'Bahcelievler',
  Bakırköy: 'Bakirkoy',
  Başakşehir: 'Basaksehir',
  Bayrampaşa: 'Bayrampasa',
  Beşiktaş: 'Besiktas',
  Beykoz: 'Beykoz',
  Beylikdüzü: 'Beylikduzu',
  Beyoğlu: 'Beyoglu',
  Büyükçekmece: 'Buyukcekmece',
  Çatalca: 'Catalca',
  Çekmeköy: 'Cekmekoy',
  Esenler: 'Esenler',
  Esenyurt: 'Esenyurt',
  Eyüpsultan: 'Eyupsultan',
  Fatih: 'Fatih',
  Gaziosmanpaşa: 'Gaziosmanpasa',
  Güngören: 'Gungoren',
  Kadıköy: 'Kadikoy',
  Kağıthane: 'Kagithane',
  Kartal: 'Kartal',
  Küçükçekmece: 'Kucukcekmece',
  Maltepe: 'Maltepe',
  Pendik: 'Pendik',
  Sancaktepe: 'Sancaktepe',
  Sarıyer: 'Sariyer',
  Silivri: 'Silivri',
  Şile: 'Sile',
  Şişli: 'Sisli',
  Sultanbeyli: 'Sultanbeyli',
  Sultangazi: 'Sultangazi',
  Tuzla: 'Tuzla',
  Ümraniye: 'Umraniye',
  Üsküdar: 'Uskudar',
  Zeytinburnu: 'Zeytinburnu',
};

export function getDistrictCenter(district: string): { lat: number; lng: number } {
  const key = DISTRICT_NAME_TO_COORD_KEY[district];
  if (!key) return { lat: 41.0082, lng: 28.9784 };
  return ISTANBUL_DISTRICT_COORDINATES[key];
}
function readFields(): Field[] {
  try {
    const raw = localStorage.getItem(FIELDS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Field[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeFields(list: Field[]) {
  localStorage.setItem(FIELDS_KEY, JSON.stringify(list));
  syncFieldsToSupabase();
  notifyLocalDataChanged();
}

export const fields: Field[] = readFields();

export function refreshFieldsFromStorage(): void {
  const latest = readFields();
  fields.splice(0, fields.length, ...latest);
}

function getDayKey(date: string): DayKey {
  const day = new Date(date).getDay();
  const keys: DayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  return keys[day] || 'mon';
}

function parseTimeToMinutes(value: string): number | null {
  const [hStr, mStr] = value.trim().split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  if (!Number.isInteger(h) || !Number.isInteger(m) || h < 0 || h > 24 || m < 0 || m > 59) return null;
  if (h === 24 && m !== 0) return null;
  return h * 60 + m;
}

function toHourNumber(minutes: number): number {
  return Number((minutes / 60).toFixed(2));
}

function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function getRangesForField(field: Field): Array<{ start: number; end: number }> {
  if (field.availableTimeRanges) {
    const ranges = field.availableTimeRanges
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .flatMap((part) => {
        const [startRaw, endRaw] = part.split('-').map((i) => i.trim());
        if (!startRaw || !endRaw) return [];
        const start = parseTimeToMinutes(startRaw);
        const end = parseTimeToMinutes(endRaw);
        if (start === null || end === null || end === start) return [];
        // Overnight support: 22:00-03:00 => [22:00-24:00] + [00:00-03:00]
        if (end < start) {
          return [
            { start, end: 24 * 60 },
            { start: 0, end },
          ];
        }
        return [{ start, end }];
      })
      .filter((v): v is { start: number; end: number } => Boolean(v));
    if (ranges.length > 0) return ranges;
  }
  return [{ start: field.openingHour * 60, end: field.closingHour * 60 }];
}

export function generateTimeSlots(fieldId: string, date: string): TimeSlot[] {
  const field = fields.find(f => f.id === fieldId);
  if (!field) return [];

  const slots: TimeSlot[] = [];
  const startInterval = 60;
  const sessionDuration = 60;
  let ranges = getRangesForField(field);
  if (field.availableTimeRangesByDay) {
    const dayKey = getDayKey(date);
    const dayRangeRaw = field.availableTimeRangesByDay[dayKey];
    if (dayRangeRaw) {
      const tempField = { ...field, availableTimeRanges: dayRangeRaw };
      ranges = getRangesForField(tempField);
    }
  }

  for (const range of ranges) {
    const alignedStart = Math.ceil(range.start / 60) * 60;
    for (let minute = alignedStart; minute + sessionDuration <= range.end; minute += startInterval) {
      const hour = toHourNumber(minute);
      const endMinute = minute + sessionDuration;
      const isBooked = preBookedSlots.some(
        s => s.fieldId === fieldId && s.date === date && s.hour === hour
      );
      slots.push({
        fieldId,
        date,
        hour,
        label: `${formatMinutes(minute)}-${formatMinutes(endMinute)}`,
        isBooked,
        bookedBy: isBooked ? 'Rezerve' : undefined,
      });
    }
  }
  return slots;
}

export const preBookedSlots: TimeSlot[] = [];

export const bookings: Booking[] = [];

export const ownerStats: OwnerStats = {
  totalRevenue: 187500,
  monthlyRevenue: 34200,
  totalBookings: 456,
  monthlyBookings: 78,
  occupancyRate: 72,
  averageRating: 4.6,
};

export const districts = Object.keys(DISTRICT_NAME_TO_COORD_KEY).sort((a, b) =>
  a.localeCompare(b, 'tr')
);

type NewFieldInput = Omit<Field, 'id' | 'rating' | 'reviewCount' | 'lat' | 'lng'> & {
  lat?: number;
  lng?: number;
  rating?: number;
  reviewCount?: number;
};

export function addField(input: NewFieldInput): Field {
  const maxNumericId = fields.reduce((max, field) => {
    const value = Number(field.id);
    if (Number.isNaN(value)) {
      return max;
    }
    return Math.max(max, value);
  }, 0);

  const newField: Field = {
    ...input,
    ...(input.lat !== undefined && input.lng !== undefined
      ? { lat: input.lat, lng: input.lng }
      : getDistrictCenter(input.district)),
    id: String(maxNumericId + 1),
    rating: input.rating ?? 0,
    reviewCount: input.reviewCount ?? 0,
    images: input.images.length > 0 ? input.images : ['https://images.unsplash.com/photo-1529900748604-07564a03e7a6?w=800'],
    amenities: {
      ...createDefaultAmenities(),
      ...input.amenities,
    },
    slotIntervalMinutes: input.slotIntervalMinutes ?? 60,
    sizeOptions: input.sizeOptions && input.sizeOptions.length > 0 ? input.sizeOptions : [input.size],
    subscriptionPlan: input.subscriptionPlan ?? 'free',
    subscriptionEnabled: input.subscriptionEnabled ?? false,
    subscriptionPrice: input.subscriptionPrice ?? 0,
    subscriptionRules: input.subscriptionRules,
    fieldType: input.fieldType ?? 'indoor',
    isReservationPaused: input.isReservationPaused ?? false,
    pricingByTimeRange: input.pricingByTimeRange ?? [],
  };

  fields.unshift(newField);
  writeFields(fields);
  logAuditEvent({
    session: getSession(),
    action: 'field.created',
    targetType: 'field',
    targetId: newField.id,
    meta: { ownerId: newField.ownerId, district: newField.district },
  });
  return newField;
}

export function createFieldWithAuth(input: NewFieldInput): { ok: true; field: Field } | { ok: false; error: string } {
  const session = getSession();
  if (!session || session.role !== 'owner') {
    return { ok: false, error: 'Saha eklemek için sahip hesabı ile giriş yapmalısınız.' };
  }
  const owner = getOwnerProfileById(session.ownerId);
  if (!owner || owner.status !== 'approved') {
    return { ok: false, error: 'Saha eklemek için admin onaylı sahip profili gerekli.' };
  }
  if (input.ownerId !== session.ownerId) {
    return { ok: false, error: 'Sadece kendi sahip hesabınız adına saha ekleyebilirsiniz.' };
  }
  const field = addField(input);
  return { ok: true, field };
}

export function updateFieldById(
  fieldId: string,
  payload: Partial<Pick<Field, 'name' | 'district' | 'address' | 'phone' | 'pricePerHour' | 'size' | 'sizeOptions' | 'turfType' | 'openingHour' | 'closingHour' | 'description' | 'availableTimeRanges' | 'availableTimeRangesByDay' | 'slotIntervalMinutes' | 'mapsUrl' | 'locationInputType' | 'lat' | 'lng' | 'subscriptionPlan' | 'subscriptionEnabled' | 'subscriptionPrice' | 'subscriptionRules' | 'fieldType' | 'isReservationPaused' | 'reservationPausedMessage' | 'pricingByTimeRange' | 'width' | 'length' | 'roofType' | 'hasScoreboard' | 'hasGoalNets' | 'maxCapacity' | 'waterPrice' | 'shoeRentalPrice' | 'paymentIban' | 'paymentBankName' | 'paymentAccountName' | 'paymentNote' | 'images' | 'amenities'>>
): Field | null {
  const field = fields.find((item) => item.id === fieldId);
  if (!field) return null;

  Object.assign(field, payload);
  if (payload.district) {
    const center = getDistrictCenter(payload.district);
    if (!payload.lat && !payload.lng) {
      field.lat = center.lat;
      field.lng = center.lng;
    }
  }
  writeFields(fields);
  logAuditEvent({
    session: getSession(),
    action: 'field.updated',
    targetType: 'field',
    targetId: field.id,
    meta: { keys: Object.keys(payload) },
  });
  return field;
}

/** Get effective price for a given hour, considering time-based pricing */
export function getPriceForHour(field: Field, hour: number): number {
  if (field.pricingByTimeRange && field.pricingByTimeRange.length > 0) {
    for (const range of field.pricingByTimeRange) {
      if (hour >= range.startHour && hour < range.endHour) {
        return range.price;
      }
    }
  }
  return field.pricePerHour;
}

/** Price shown to customers in listing/detail cards (always evening/high tariff) */
export function getDisplayPrice(field: Field): number {
  if (!field.pricingByTimeRange || field.pricingByTimeRange.length === 0) {
    return field.pricePerHour;
  }

  const eveningRange = field.pricingByTimeRange.find((range) => {
    const label = (range.label || '').toLocaleLowerCase('tr');
    return label.includes('akşam') || (range.startHour <= 18 && range.endHour >= 22);
  });
  if (eveningRange) return eveningRange.price;

  const allPrices = [field.pricePerHour, ...field.pricingByTimeRange.map((range) => range.price)];
  return Math.max(...allPrices);
}

/** Delete a field by ID (only in-memory) */
export function deleteFieldById(fieldId: string): boolean {
  const idx = fields.findIndex((f) => f.id === fieldId);
  if (idx < 0) return false;
  fields.splice(idx, 1);
  writeFields(fields);
  logAuditEvent({
    session: getSession(),
    action: 'field.deleted',
    targetType: 'field',
    targetId: fieldId,
  });
  return true;
}

export function cancelBookingById(bookingId: string): boolean {
  const booking = bookings.find((item) => item.id === bookingId);
  if (!booking) return false;
  booking.status = 'cancelled';
  return true;
}

fields.forEach((field) => {
  if (!field.sizeOptions || field.sizeOptions.length === 0) {
    field.sizeOptions = [field.size];
  }
  if (!field.slotIntervalMinutes) {
    field.slotIntervalMinutes = 60;
  }
  if (!field.subscriptionPlan) {
    field.subscriptionPlan = 'free';
  }
  if (typeof field.subscriptionEnabled !== 'boolean') {
    field.subscriptionEnabled = false;
  }
  if (typeof field.subscriptionPrice !== 'number') {
    field.subscriptionPrice = 0;
  }
});
writeFields(fields);
